<template>
  <HLDrawer
    :id="id"
    :show="show"
    placement="right"
    :width="resolvedWidth"
    :show-mask="showMask"
    :mask-closable="maskClosable"
    :close-on-esc="closeOnEsc"
    :trap-focus="true"
    :block-scroll="true"
    :native-scrollbar="false"
    display-directive="if"
    @update:show="val => !val && $emit('close')"
  >
    <HLDrawerContent :id="`${id}-content`" :closable="true" :show-header="false" :native-scrollbar="false" body-class="kaizen-drawer-body">
      <!-- Custom header -->
      <template #header>
        <div :class="['kaizen-drawer-header', { 'kaizen-drawer-header--no-divider': !headerDivider }]">
          <div v-if="$slots.icon" :class="['kaizen-drawer-icon', iconBgClass]">
            <HLIcon :size="20" :class="iconColorClass">
              <slot name="icon" />
            </HLIcon>
          </div>
          <div class="kaizen-drawer-title-block">
            <HLText size="xl" weight="semibold" html-tag="span">
              {{ title }}
            </HLText>
            <HLText v-if="subtitle" variant="body-sm" color="muted" class="block mt-0.5">
              {{ subtitle }}
            </HLText>
          </div>
          <div class="kaizen-drawer-header-actions">
            <slot name="header-actions" />
            <HLButton variant="ghost" size="xs" class="kaizen-drawer-close" @click="$emit('close')">
              <template #icon>
                <MaterialCloseIcon />
              </template>
            </HLButton>
          </div>
        </div>
      </template>

      <!-- Body -->
      <template #default>
        <div class="kaizen-drawer-content">
          <slot />
        </div>
      </template>

      <!-- Footer -->
      <template v-if="hasFooter" #footer>
        <div :class="['kaizen-drawer-footer', { 'kaizen-drawer-footer--no-divider': !footerDivider }]">
          <div class="kaizen-drawer-footer-left">
            <slot name="footer-left" />
          </div>
          <div class="kaizen-drawer-footer-right">
            <slot name="footer" />
          </div>
        </div>
      </template>
    </HLDrawerContent>
  </HLDrawer>
</template>

<script setup>
  import { computed, useSlots } from 'vue'
  import { HLDrawer, HLDrawerContent, HLText, HLIcon, HLButton } from '@platform-ui/highrise'
  import { MaterialCloseIcon } from '@gohighlevel/ghl-icons/24/material/rounded'

  const SIZE_MAP = {
    sm: 400,
    md: 600,
    lg: 800,
  }

  const props = defineProps({
    id: { type: String, required: true },
    show: { type: Boolean, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    /** 'sm' (400px) | 'md' (600px) | 'lg' (80vw) | number | string */
    width: { type: [String, Number], default: 'md' },
    /** primary | red | green | orange | blue */
    iconColor: { type: String, default: 'primary' },
    maskClosable: { type: Boolean, default: true },
    /** Dim/blocking backdrop behind the drawer. Set false for non-modal panels. */
    showMask: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true },
    headerDivider: { type: Boolean, default: true },
    footerDivider: { type: Boolean, default: true },
  })

  defineEmits(['close'])

  const slots = useSlots()
  const hasFooter = computed(() => !!slots.footer || !!slots['footer-left'])

  const resolvedWidth = computed(() => {
    if (SIZE_MAP[props.width]) return SIZE_MAP[props.width]
    return props.width
  })

  const BG_MAP = {
    primary: 'bg-primary-50 dark:bg-primary-900/30',
    red: 'bg-red-50 dark:bg-red-900/20',
    green: 'bg-green-50 dark:bg-green-900/20',
    orange: 'bg-orange-50 dark:bg-orange-900/20',
    blue: 'bg-blue-50 dark:bg-blue-900/20',
  }

  const COLOR_MAP = {
    primary: 'text-primary-600 dark:text-primary-400',
    red: 'text-red-600 dark:text-red-400',
    green: 'text-green-600 dark:text-green-400',
    orange: 'text-orange-600 dark:text-orange-400',
    blue: 'text-blue-600 dark:text-blue-400',
  }

  const iconBgClass = computed(() => BG_MAP[props.iconColor] ?? BG_MAP.primary)
  const iconColorClass = computed(() => COLOR_MAP[props.iconColor] ?? COLOR_MAP.primary)
</script>

<style scoped>
  .kaizen-drawer-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
  }

  .dark .kaizen-drawer-header {
    border-bottom-color: var(--gray-700, #374151);
  }

  .kaizen-drawer-header--no-divider {
    border-bottom: none;
  }

  .kaizen-drawer-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .kaizen-drawer-title-block {
    flex: 1;
    min-width: 0;
    padding-top: 2px;
  }

  .kaizen-drawer-header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .kaizen-drawer-close {
    color: var(--gray-400, #9ca3af);
  }

  .kaizen-drawer-content {
    flex: 1;
    overflow-y: auto;
    padding: 14px 16px;
  }

  /* Normalize HLFormItem label typography inside drawers */
  :deep(.hr-form-item-label),
  :deep(.hr-form-item-label__text),
  :deep(.hr-form-custom-label) {
    text-transform: none !important;
    letter-spacing: normal !important;
    color: var(--neutral-900) !important;
  }

  .kaizen-drawer-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    /* padding: 10px 16px; */
    /* border-top: 1px solid var(--gray-200, #e5e7eb); */
  }

  .dark .kaizen-drawer-footer {
    border-top-color: var(--gray-700, #374151);
  }

  .kaizen-drawer-footer--no-divider {
    border-top: none;
  }

  .kaizen-drawer-footer-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }

  .kaizen-drawer-footer-left:empty {
    display: none;
  }

  .kaizen-drawer-footer-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }
</style>
